import { useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertBanner, Box, Button, Card, Loader, Segmented, Stack, Text, Title } from '@grovus/design-system'
import { GoogleLogo } from '../auth/GoogleLogo'
import { AuthLayout } from '../auth/AuthLayout'
import { startGoogleSignIn, type Membership, type SessionUser } from '../auth/api'
import type { Intent, InviteTerminal } from '../auth/entry'
import type { Reconciled } from '../auth/reconcile'
import { rememberSignInReturn } from '../auth/signInReturn'
import '../auth/google-button.css'
import { lookupInviteLink, type InviteLink } from '../data/members'
import { AUTH_COPY, LANG_OPTIONS, MEMBERS_COPY, type Dict, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import type { Theme } from '../lib/preferences'
import { InvitationList } from './InvitationList'
import { clearInviteToken, heldInviteToken } from './inviteToken'
import type { Invitations } from './useInvitations'

interface Props {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
  /** `null` while signed out: the lookup needs no session. */
  user: SessionUser | null
  memberships: Membership[]
  invitations: Invitations
  onReconcile: (intent?: Intent) => Promise<Reconciled>
  /** A terminal page rendered: the boundary records it and clears the token. */
  onTerminal: (kind: InviteTerminal) => void
  /** Continue, or Open <client>: the boundary's ordinary decision, on `companyId` when given. */
  onLeave: (companyId?: string) => void
  /** "Use another account": the sign-out that keeps the token. */
  onUseAnotherAccount: () => Promise<'ok' | 'error'>
}

type Lookup = { phase: 'loading' } | { phase: 'unavailable' } | { phase: 'notFound' } | { phase: 'ok'; link: InviteLink }

/** Which board of the design's state table shows, in the table's order. */
type Board =
  | { kind: 'checking' }
  | { kind: 'unavailable'; retry: () => void }
  | { kind: InviteTerminal | 'unknown'; link: InviteLink | null }
  | { kind: 'signedout'; link: InviteLink }
  | { kind: 'match'; link: InviteLink }
  | { kind: 'other'; link: InviteLink }

/**
 * The invite link page (GRO-106 design, screen 4), at `/invite`. It looks the held token up
 * without a session, so a signed-out visitor sees the company, the role and the masked
 * email before signing in. The lookup runs again when the visitor signs out, so "Use
 * another account" lands on the signed-out board from a fresh answer.
 */
export function InviteLinkPage({
  t, lang, setLang, theme, toggleTheme, user, memberships, invitations, onReconcile, onTerminal, onLeave, onUseAnotherAccount,
}: Props) {
  const copy = MEMBERS_COPY[lang]
  const [lookup, setLookup] = useState<Lookup>({ phase: 'loading' })
  const [attempt, setAttempt] = useState(0)
  const [notice, setNotice] = useState(false)
  const [pending, setPending] = useState(false)
  const signedIn = user !== null

  useEffect(() => {
    const token = heldInviteToken()
    // A terminal page cleared the token and keeps showing what its lookup said.
    if (token === null) return
    let current = true
    setLookup({ phase: 'loading' })
    void lookupInviteLink(token).then((result) => {
      if (!current) return
      setLookup(result.status === 'ok' ? { phase: 'ok', link: result.link } : { phase: result.status })
    })
    return () => { current = false }
  }, [signedIn, attempt])

  // Once the invitation matched, the list stays on screen when the shared list drops it,
  // so an Accept or a terminal refusal keeps its outcome and its Retry.
  const [matched, setMatched] = useState<string | null>(null)
  const board = decide(lookup, user, memberships, invitations, matched, () => setAttempt((n) => n + 1))
  const matchId = board.kind === 'match' ? board.link.inviteId : null
  useEffect(() => {
    if (matchId !== null) setMatched(matchId)
  }, [matchId])

  const reported = useRef(false)
  useEffect(() => {
    if (reported.current) return
    const kind = board.kind
    if (kind === 'member' || kind === 'expired' || kind === 'revoked' || kind === 'used' || kind === 'unknown') {
      reported.current = true
      onTerminal(kind === 'unknown' ? 'revoked' : kind)
    }
  }, [board.kind, onTerminal])

  async function signIn() {
    setPending(true)
    setNotice(false)
    const result = await startGoogleSignIn()
    if (result.status === 'ok') {
      rememberSignInReturn(window.location.pathname)
      window.location.assign(result.url)
      return
    }
    setPending(false)
    setNotice(true)
  }

  async function useAnother() {
    setPending(true)
    setNotice(false)
    const result = await onUseAnotherAccount()
    setPending(false)
    if (result === 'error') setNotice(true)
  }

  const page = (title: string, lede: string | null, action: ReactNode) => (
    <Card>
      <Stack gap={12}>
        <Title order={1} fz="xl" style={{ overflowWrap: 'anywhere' }}>{title}</Title>
        {lede !== null && lede !== '' && <Text className="grv-muted" style={{ overflowWrap: 'anywhere' }}>{lede}</Text>}
        {notice && <AlertBanner tone="bad">{AUTH_COPY[lang].service_unavailable}</AlertBanner>}
        {action !== null && <Box mt={6}>{action}</Box>}
      </Stack>
    </Card>
  )
  const goOn = (companyId?: string, label = copy.goToGrovus) => (
    <Button variant={companyId === undefined ? 'default' : 'filled'} onClick={() => onLeave(companyId)}>{label}</Button>
  )

  let body: ReactNode
  switch (board.kind) {
    case 'checking':
      body = <Card><Text fz="sm" role="status">{copy.linkChecking}</Text></Card>
      break
    case 'unavailable':
      body = (
        <Card>
          <Text fz="sm" role="alert">{AUTH_COPY[lang].service_unavailable}</Text>
          <Button mt="md" onClick={board.retry}>{t.retry}</Button>
        </Card>
      )
      break
    case 'member':
      body = page(fmt(copy.linkMember, { client: board.link!.companyName }), null,
        goOn(board.link!.companyId, fmt(copy.open, { client: board.link!.companyName })))
      break
    case 'expired':
      body = page(copy.invExpired, fmt(copy.linkExpiredLede, { inviter: board.link!.inviterEmail }), goOn())
      break
    case 'revoked':
      body = page(copy.invRevoked, fmt(copy.linkRevokedLede, { client: board.link!.companyName }), goOn())
      break
    case 'unknown':
      body = page(copy.invRevoked, copy.linkUnknownLede, goOn())
      break
    case 'used':
      body = page(copy.invUsed, null, goOn())
      break
    case 'signedout': {
      const { link } = board
      body = page(
        fmt(copy.linkInvited, { client: link.companyName }),
        fmt(copy.linkInvitedLede, { inviter: link.inviterEmail, email: link.maskedEmail, role: copy.roles[link.role] }),
        <button type="button" className="google-signin-btn" disabled={pending} aria-busy={pending} onClick={() => { void signIn() }}>
          {pending ? <Loader type="oval" size={18} color="currentColor" /> : <GoogleLogo />}
          <span>{copy.linkSignIn}</span>
        </button>,
      )
      break
    }
    case 'other':
      body = page(
        fmt(copy.linkOther, { email: board.link.maskedEmail }),
        fmt(copy.linkOtherLede, { email: user?.email ?? '' }),
        <Button variant="default" disabled={pending} onClick={() => { void useAnother() }}>{copy.linkUseAnother}</Button>,
      )
      break
    case 'match': {
      const items = invitations.state.phase === 'ready' ? invitations.state.items : []
      body = (
        <Stack gap={16}>
          <Title order={1} fz="xl">{copy.invitations}</Title>
          <InvitationList
            lang={lang}
            items={items}
            origin="entry"
            highlight={board.link.inviteId}
            onReconcile={onReconcile}
            invitations={invitations}
            onBeforeAccept={clearInviteToken}
            onDeclined={() => onLeave()}
            leave={<Box>{goOn()}</Box>}
            fallback={(
              <Card>
                <Text className="grv-muted">{copy.ledeNone}</Text>
                <Box mt="md">{goOn()}</Box>
              </Card>
            )}
          />
        </Stack>
      )
      break
    }
  }

  return (
    <AuthLayout
      maxWidth={520}
      topRight={(
        <>
          <Segmented
            value={lang}
            onChange={(v) => setLang(v as Lang)}
            data={LANG_OPTIONS}
          />
          <Button variant="default" onClick={toggleTheme}>
            {theme === 'light' ? t.dark : t.light}
          </Button>
        </>
      )}
    >
      <Box data-testid="invite-link" data-board={board.kind}>{body}</Box>
    </AuthLayout>
  )
}

/** The design's state table, checked in order. */
function decide(lookup: Lookup, user: SessionUser | null, memberships: Membership[], invitations: Invitations, matched: string | null, retry: () => void): Board {
  if (lookup.phase === 'loading') return { kind: 'checking' }
  if (lookup.phase === 'unavailable') return { kind: 'unavailable', retry }
  if (lookup.phase === 'notFound') return { kind: 'unknown', link: null }
  const { link } = lookup
  if (user !== null && memberships.some((m) => m.companyId === link.companyId)) return { kind: 'member', link }
  if (link.status !== 'pending') return { kind: link.status, link }
  if (user === null) return { kind: 'signedout', link }
  if (matched === link.inviteId) return { kind: 'match', link }
  const { state } = invitations
  if (state.phase === 'loading') return { kind: 'checking' }
  if (state.phase === 'failed') return { kind: 'unavailable', retry: () => { void invitations.refetch() } }
  return state.items.some((i) => i.id === link.inviteId) ? { kind: 'match', link } : { kind: 'other', link }
}
